import { useState, FormEvent } from 'react';
import { KeyRound, Lock, Mail } from 'lucide-react';
import { requestLoginCode, setApiKey, validateApiKey, verifyLoginCode } from '../services/api';
import { errorMessage, operatorLogin } from '../services/operatorApi';

export type Mode = 'operator' | 'client';

const inputClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20';

export default function LoginPage({ onLoggedIn }: { onLoggedIn: (mode: Mode) => void }) {
  const [mode, setMode] = useState<Mode>('client');
  const [secret, setSecret] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [useKey, setUseKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setSecret('');
    setError(null);
    setNotice(null);
    setStep('email');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'operator') {
        await operatorLogin(secret);
        onLoggedIn('operator');
      } else if (useKey) {
        if (await validateApiKey(secret.trim())) {
          setApiKey(secret.trim());
          onLoggedIn('client');
        } else {
          setError('Clé API refusée');
        }
      } else if (step === 'email') {
        await requestLoginCode(email.trim());
        setStep('code');
        setSecret('');
        setNotice(`Si un compte existe pour ${email.trim()}, un code à 6 chiffres vient d'être envoyé. Il est valable 10 minutes.`);
      } else {
        await verifyLoginCode(email.trim(), secret.trim());
        onLoggedIn('client');
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const operator = mode === 'operator';
  const codeStep = !operator && !useKey && step === 'code';
  const emailStep = !operator && !useKey && step === 'email';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <img src="/favicon.svg" alt="" className="mx-auto mb-3 h-16 w-16" />
          <h1 className="text-2xl font-semibold text-white">Juriseal</h1>
          <p className="text-sm text-slate-400">{operator ? "Espace d'administration" : 'Bienvenue dans votre espace client'}</p>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-xl">
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 text-sm font-medium">
            {(['client', 'operator'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  reset();
                }}
                className={`rounded-md py-2 transition-colors ${mode === m ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {m === 'operator' ? 'Exploitant' : 'Client'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {emailStep && (
              <div>
                <label className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-700" htmlFor="email">
                  <Mail className="h-4 w-4" />
                  Votre adresse e-mail
                </label>
                <input id="email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className={inputClass} placeholder="vous@societe.fr" required autoFocus />
              </div>
            )}

            {!emailStep && (
              <div>
                <label className="mb-1 flex items-center gap-2 text-sm font-medium text-slate-700" htmlFor="secret">
                  {operator ? <Lock className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                  {operator ? 'Mot de passe' : useKey ? "Clé API de l'organisation" : 'Code reçu par e-mail'}
                </label>
                <input
                  id="secret"
                  type={codeStep ? 'text' : 'password'}
                  inputMode={codeStep ? 'numeric' : undefined}
                  maxLength={codeStep ? 6 : undefined}
                  autoComplete={operator ? 'current-password' : codeStep ? 'one-time-code' : 'off'}
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                  className={`${inputClass} ${operator ? '' : 'font-mono'} ${codeStep ? 'text-center text-xl tracking-[0.4em]' : ''}`}
                  placeholder={useKey ? 'sk_live_...' : codeStep ? '······' : ''}
                  required
                  autoFocus
                />
              </div>
            )}

            {notice && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p>}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading || (emailStep ? !email.trim() : !secret.trim())}
              className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Patientez…' : operator || useKey ? 'Se connecter' : emailStep ? 'Recevoir un code' : 'Valider le code'}
            </button>

            {operator ? (
              <p className="text-xs text-slate-500">Accès réservé à l’exploitant du service. La session dure 8 heures.</p>
            ) : (
              <div className="space-y-2 text-xs text-slate-500">
                {codeStep && (
                  <button type="button" className="block text-indigo-600 hover:underline" onClick={reset}>
                    Changer d&apos;adresse ou renvoyer un code
                  </button>
                )}
                {useKey ? <p>La clé reste dans cet onglet et disparaît à sa fermeture.</p> : <p>Utilisez l&apos;adresse e-mail de votre commande ou de votre compte. Aucun mot de passe à retenir.</p>}
                <button
                  type="button"
                  className="text-slate-600 underline"
                  onClick={() => {
                    setUseKey(!useKey);
                    reset();
                  }}
                >
                  {useKey ? 'Me connecter avec mon e-mail' : "J'ai une clé d'API et je préfère l'utiliser"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
